import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AI_REVIEW_STATUS,
  EXECUTION_STATUS,
  PRISMA_EXECUTION_STATUS,
  type AiReviewStatus,
  type ExecutionStatus,
  type RequirementReviewStatus,
  type SpecSectionDslStatus,
} from "@pericles/shared";
import {
  Card,
  DataTable,
  EmptyState,
  PageHeader,
  type DataTableColumn,
} from "@pericles/framework-frontend";

import {
  createProjectSpecSection,
  deleteProjectSpecSection,
  fetchProjectSpecSections,
  acceptDslFragmentForSpecSection,
  suggestDslForSpecSection,
  updateProjectSpecSectionAiReviewStatus,
  updateProjectSpecSection,
} from "../api/projectSpecSectionsClient";
import type {
  CreateProjectSpecSectionInput,
  ProjectSpecSectionDetail,
  ProjectSpecSectionSummary,
} from "../types/specs";
import {
  AI_REVIEW_STATUS_UI,
  normalizeExecutionStatus,
} from "../domain/statusAdapters";

import { InlineAlert } from "./InlineAlert";
import { DeleteButton } from "./DeleteButton";
import {
  AiReviewStatusBadge,
  RequirementReviewStatusBadge,
  ExecutionStatusBadge,
  REVIEW_STATUS_BADGES,
} from "./RequirementReviewStatusBadge";
import { Button, Drawer, DrawerContent, Input, Select, Textarea } from "./ui";

interface ProjectSpecSectionsTabProps {
  projectId: string;
}

function formatDate(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

const AI_REVIEW_OPTIONS: Array<{ value: AiReviewStatus; label: string }> = [
  { value: AI_REVIEW_STATUS.pending, label: AI_REVIEW_STATUS_UI[AI_REVIEW_STATUS.pending].label },
  { value: AI_REVIEW_STATUS.in_progress, label: AI_REVIEW_STATUS_UI[AI_REVIEW_STATUS.in_progress].label },
  { value: AI_REVIEW_STATUS.completed, label: AI_REVIEW_STATUS_UI[AI_REVIEW_STATUS.completed].label },
  { value: AI_REVIEW_STATUS.rejected, label: AI_REVIEW_STATUS_UI[AI_REVIEW_STATUS.rejected].label },
  { value: AI_REVIEW_STATUS.none, label: AI_REVIEW_STATUS_UI[AI_REVIEW_STATUS.none].label },
];

const REVIEW_STATUS_OPTIONS: Array<{ value: RequirementReviewStatus; label: string }> = Object.entries(
  REVIEW_STATUS_BADGES,
).map(([value, config]) => ({ value: value as RequirementReviewStatus, label: config.label }));

const DSL_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "NOT_STARTED", label: "Not started" },
  { value: "CANDIDATE", label: "Candidate" },
  { value: "ACCEPTED", label: "Accepted" },
  { value: "OUTDATED", label: "Outdated" },
];

type SpecProvenance = "document" | "meeting" | "ai";

const PROVENANCE_FILTER_OPTIONS: Array<{ value: SpecProvenance | "all"; label: string }> = [
  { value: "all", label: "All" },
  { value: "document", label: "Document" },
  { value: "meeting", label: "Meeting" },
  { value: "ai", label: "IA" },
];

const DSL_TO_EXECUTION: Record<string, ExecutionStatus> = {
  NOT_STARTED: PRISMA_EXECUTION_STATUS.planned,
  CANDIDATE: PRISMA_EXECUTION_STATUS.inProgress,
  ACCEPTED: PRISMA_EXECUTION_STATUS.done,
  OUTDATED: PRISMA_EXECUTION_STATUS.dropped,
};

interface SpecSectionFormState {
  title: string;
  category: string;
  source: string;
  content: string;
  reviewStatus: RequirementReviewStatus;
  aiReviewStatus: AiReviewStatus;
  relatedRequirementIds: string;
  relatedChecklistItemIds: string;
  relatedWorkItemIds: string;
  relatedMeetingIds: string;
  relatedAiArtifactIds: string;
}

function toInputPayload(values: SpecSectionFormState): CreateProjectSpecSectionInput {
  const normalizeIds = (value: string): string[] =>
    value
      .split(/[,\n]/)
      .map((entry) => entry.trim())
      .filter(Boolean);

  return {
    title: values.title.trim(),
    category: values.category.trim() || null,
    source: values.source.trim() || "manual",
    reviewStatus: values.reviewStatus,
    aiReviewStatus: values.aiReviewStatus,
    relatedRequirementIds: normalizeIds(values.relatedRequirementIds),
    relatedChecklistItemIds: normalizeIds(values.relatedChecklistItemIds),
    relatedWorkItemIds: normalizeIds(values.relatedWorkItemIds),
    relatedMeetingIds: normalizeIds(values.relatedMeetingIds),
    relatedAiArtifactIds: normalizeIds(values.relatedAiArtifactIds),
    content: values.content.trim(),
  };
}

function formatIdList(ids?: string[] | null): string {
  return (ids ?? []).join(", ");
}

export function mapSpecProvenance(source?: string | null, specSource?: string | null): SpecProvenance | null {
  const value = source ?? specSource;
  if (!value) return null;
  const normalized = value.toLowerCase();
  if (normalized.includes("meeting")) return "meeting";
  if (normalized.includes("ai")) return "ai";
  return "document";
}

export function toExecutionStatus(status?: string | null): ExecutionStatus {
  return normalizeExecutionStatus(status) ?? DSL_TO_EXECUTION[status ?? ""] ?? PRISMA_EXECUTION_STATUS.planned;
}

interface SpecSectionFormProps {
  open: boolean;
  mode: "create" | "edit";
  values: SpecSectionFormState;
  onChange: (values: SpecSectionFormState) => void;
  onCancel: () => void;
  onSubmit: () => void;
  error?: string | null;
  isSaving?: boolean;
}

function SpecSectionForm({
  open,
  mode,
  values,
  onChange,
  onCancel,
  onSubmit,
  error,
  isSaving,
}: SpecSectionFormProps): JSX.Element | null {
  return (
    <Drawer open={open} onOpenChange={(next) => (!next ? onCancel() : null)}>
      <DrawerContent className="p-0">
        <div className="flex items-center justify-between border-b p-4">
          <div>
            <div className="text-sm font-semibold uppercase text-slate-500">{mode === "create" ? "Create" : "Edit"}</div>
            <div className="text-lg font-bold text-slate-900">
              {mode === "create" ? "New spec section" : "Edit spec section"}
            </div>
          </div>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Close
          </Button>
        </div>

        <div className="flex flex-col gap-4 p-4">
          {error ? <InlineAlert tone="danger" title={error} /> : null}
          <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
            Title
            <Input
              type="text"
              required
              value={values.title}
              onChange={(event) => onChange({ ...values, title: event.target.value })}
            />
          </label>

          <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
            Category
            <Input
              type="text"
              placeholder="Functional area, module, or theme"
              value={values.category}
              onChange={(event) => onChange({ ...values, category: event.target.value })}
            />
          </label>

          <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
            Source
            <Select value={values.source} onChange={(event) => onChange({ ...values, source: event.target.value })}>
              <option value="manual">Manual</option>
              <option value="ai">AI</option>
              <option value="meeting_ai">Meeting AI</option>
              <option value="imported">Imported</option>
            </Select>
          </label>

          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
              Review status
              <Select
                value={values.reviewStatus}
                onChange={(event) =>
                  onChange({ ...values, reviewStatus: event.target.value as RequirementReviewStatus })
                }
              >
                {REVIEW_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
              AI review status
              <Select
                value={values.aiReviewStatus}
                onChange={(event) =>
                  onChange({ ...values, aiReviewStatus: event.target.value as AiReviewStatus })
                }
              >
                {AI_REVIEW_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </label>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
              Related requirements
              <Input
                type="text"
                placeholder="IDs separated by commas"
                value={values.relatedRequirementIds}
                onChange={(event) => onChange({ ...values, relatedRequirementIds: event.target.value })}
              />
            </label>

            <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
              Related checklist items
              <Input
                type="text"
                placeholder="IDs separated by commas"
                value={values.relatedChecklistItemIds}
                onChange={(event) => onChange({ ...values, relatedChecklistItemIds: event.target.value })}
              />
            </label>

            <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
              Related work items
              <Input
                type="text"
                placeholder="Workplan IDs separated by commas"
                value={values.relatedWorkItemIds}
                onChange={(event) => onChange({ ...values, relatedWorkItemIds: event.target.value })}
              />
            </label>

            <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
              Related meetings
              <Input
                type="text"
                placeholder="Meeting IDs separated by commas"
                value={values.relatedMeetingIds}
                onChange={(event) => onChange({ ...values, relatedMeetingIds: event.target.value })}
              />
            </label>

            <label className="flex flex-col gap-1 text-sm font-medium text-slate-700 md:col-span-2">
              Related AI artifacts
              <Input
                type="text"
                placeholder="Artifacts or run IDs separated by commas"
                value={values.relatedAiArtifactIds}
                onChange={(event) => onChange({ ...values, relatedAiArtifactIds: event.target.value })}
              />
            </label>
          </div>

          <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
            Content
            <Textarea
              className="min-h-[180px]"
              placeholder="Describe the functional and technical specification for this section…"
              value={values.content}
              onChange={(event) => onChange({ ...values, content: event.target.value })}
            />
          </label>

          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={onSubmit}
              disabled={isSaving || !values.title.trim() || !values.content.trim()}
            >
              {isSaving ? "Saving…" : mode === "create" ? "Create section" : "Save changes"}
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export function ProjectSpecSectionsTab({ projectId }: ProjectSpecSectionsTabProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [sections, setSections] = useState<ProjectSpecSectionDetail[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [formState, setFormState] = useState<SpecSectionFormState>({
    title: "",
    category: "",
    source: "manual",
    reviewStatus: "pending_review",
    aiReviewStatus: "none",
    relatedRequirementIds: "",
    relatedChecklistItemIds: "",
    relatedWorkItemIds: "",
    relatedMeetingIds: "",
    relatedAiArtifactIds: "",
    content: "",
  });
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [updatingReviewId, setUpdatingReviewId] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [updatingAiReviewId, setUpdatingAiReviewId] = useState<string | null>(null);
  const [aiReviewError, setAiReviewError] = useState<string | null>(null);
  const [updatingDslId, setUpdatingDslId] = useState<string | null>(null);
  const [dslError, setDslError] = useState<string | null>(null);
  const [suggestingDslId, setSuggestingDslId] = useState<string | null>(null);
  const [dslSuggestError, setDslSuggestError] = useState<string | null>(null);
  const [dslSuggestErrorId, setDslSuggestErrorId] = useState<string | null>(null);
  const [acceptingDslId, setAcceptingDslId] = useState<string | null>(null);
  const [dslAcceptError, setDslAcceptError] = useState<string | null>(null);
  const [dslAcceptErrorId, setDslAcceptErrorId] = useState<string | null>(null);
  const [deletingSectionId, setDeletingSectionId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteErrorId, setDeleteErrorId] = useState<string | null>(null);
  const [selectedReviewStatus, setSelectedReviewStatus] = useState<RequirementReviewStatus | "all">("all");
  const [selectedAiStatus, setSelectedAiStatus] = useState<AiReviewStatus | "all">("all");
  const [selectedDslStatus, setSelectedDslStatus] = useState<string | "all">("all");
  const [selectedProvenance, setSelectedProvenance] = useState<SpecProvenance | "all">("all");
  const [sortKey, setSortKey] = useState<"updated" | "review" | "ai" | "dsl" | "provenance">("updated");

  const selectedSectionIdFromUrl = searchParams.get("specSectionId");

  const updateSelectedSection = useCallback(
    (sectionId: string | null) => {
      setSelectedSectionId(sectionId);

      const nextParams = new URLSearchParams(searchParams);
      if (sectionId) {
        nextParams.set("specSectionId", sectionId);
      } else {
        nextParams.delete("specSectionId");
      }

      const nextSearch = nextParams.toString();
      navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`, { replace: true });
    },
    [location.pathname, navigate, searchParams],
  );

  const loadSections = useCallback(
    async (autoSelectId?: string) => {
      if (!projectId) {
        setSections([]);
        updateSelectedSection(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await fetchProjectSpecSections(projectId);
        setSections(result as ProjectSpecSectionDetail[]);
        if (result.length > 0) {
          const preferredId = autoSelectId ?? selectedSectionIdFromUrl;
          const targetId = result.find((section) => section.id === preferredId)?.id ?? result[0].id;
          updateSelectedSection(targetId);
        } else {
          updateSelectedSection(null);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load spec sections";
        setError(message);
      } finally {
        setIsLoading(false);
      }
    },
    [projectId, selectedSectionIdFromUrl, updateSelectedSection],
  );

  useEffect(() => {
    void loadSections();
  }, [loadSections]);

  useEffect(() => {
    if (selectedSectionIdFromUrl && selectedSectionIdFromUrl !== selectedSectionId) {
      const exists = sections.some((section) => section.id === selectedSectionIdFromUrl);
      if (exists) {
        setSelectedSectionId(selectedSectionIdFromUrl);
      }
    }
  }, [sections, selectedSectionId, selectedSectionIdFromUrl]);

  const handleUpdateAiReviewStatus = useCallback(
    async (sectionId: string, status: AiReviewStatus) => {
      if (!projectId || !sectionId) return;

      setUpdatingAiReviewId(sectionId);
      setAiReviewError(null);

      try {
        const updated = await updateProjectSpecSectionAiReviewStatus(projectId, sectionId, status);

        setSections((current) =>
          current.map((section) => (section.id === sectionId ? { ...section, ...updated } : section)),
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to update AI review status";
        setAiReviewError(message);
        alert(message);
      } finally {
        setUpdatingAiReviewId(null);
      }
    },
    [projectId],
  );

  const handleUpdateReviewStatus = useCallback(
    async (sectionId: string, status: RequirementReviewStatus) => {
      if (!projectId || !sectionId) return;

      setUpdatingReviewId(sectionId);
      setReviewError(null);

      try {
        const updated = await updateProjectSpecSection(projectId, sectionId, { reviewStatus: status });
        setSections((current) =>
          current.map((section) => (section.id === sectionId ? { ...section, ...updated } : section)),
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to update review status";
        setReviewError(message);
        alert(message);
      } finally {
        setUpdatingReviewId(null);
      }
    },
    [projectId],
  );

  const handleUpdateDslStatus = useCallback(
    async (sectionId: string, status: SpecSectionDslStatus | string) => {
      if (!projectId || !sectionId) return;

      setUpdatingDslId(sectionId);
      setDslError(null);

      try {
        const updated = await updateProjectSpecSection(projectId, sectionId, {
          dslStatus: status as SpecSectionDslStatus,
        });
        setSections((current) =>
          current.map((section) => (section.id === sectionId ? { ...section, ...updated } : section)),
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to update execution status";
        setDslError(message);
        alert(message);
      } finally {
        setUpdatingDslId(null);
      }
    },
    [projectId],
  );

  const handleSuggestDsl = useCallback(
    async (sectionId: string) => {
      if (!projectId || !sectionId) return;

      setSuggestingDslId(sectionId);
      setDslSuggestError(null);
      setDslSuggestErrorId(null);

      try {
        const updated = await suggestDslForSpecSection(projectId, sectionId);
        setSections((current) =>
          current.map((section) => (section.id === sectionId ? { ...section, ...updated } : section)),
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to suggest DSL fragments";
        setDslSuggestError(message);
        setDslSuggestErrorId(sectionId);
        alert(message);
      } finally {
        setSuggestingDslId(null);
      }
    },
    [projectId],
  );

  const handleAcceptDsl = useCallback(
    async (sectionId: string, fragment: unknown) => {
      if (!projectId || !sectionId) return;

      setAcceptingDslId(sectionId);
      setDslAcceptError(null);
      setDslAcceptErrorId(null);

      try {
        const updated = await acceptDslFragmentForSpecSection(projectId, sectionId, fragment);
        setSections((current) =>
          current.map((section) => (section.id === sectionId ? { ...section, ...updated } : section)),
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to accept DSL fragment";
        setDslAcceptError(message);
        setDslAcceptErrorId(sectionId);
        alert(message);
      } finally {
        setAcceptingDslId(null);
      }
    },
    [projectId],
  );

  const handleDeleteSection = useCallback(
    async (sectionId: string) => {
      if (!projectId || !sectionId) return;

      setDeleteError(null);
      setDeleteErrorId(null);
      setDeletingSectionId(sectionId);

      try {
        await deleteProjectSpecSection(projectId, sectionId);
        await loadSections();
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to delete spec section";
        setDeleteError(message);
        setDeleteErrorId(sectionId);
      } finally {
        setDeletingSectionId(null);
      }
    },
    [loadSections, projectId],
  );

  const selectedSection = useMemo(
    () => sections.find((section) => section.id === selectedSectionId) ?? null,
    [sections, selectedSectionId],
  );

  const filteredSections = useMemo(() => {
    let items = sections;

    if (selectedReviewStatus !== "all") {
      items = items.filter((section) => section.reviewStatus === selectedReviewStatus);
    }

    if (selectedAiStatus !== "all") {
      items = items.filter((section) => section.aiReviewStatus === selectedAiStatus);
    }

    if (selectedDslStatus !== "all") {
      items = items.filter((section) => section.dslStatus === selectedDslStatus);
    }

    if (selectedProvenance !== "all") {
      items = items.filter(
        (section) => mapSpecProvenance(section.source, section.specSource) === selectedProvenance,
      );
    }

    return items;
  }, [sections, selectedReviewStatus, selectedAiStatus, selectedDslStatus, selectedProvenance]);

  const sortedSections = useMemo(() => {
    return [...filteredSections].sort((a, b) => {
      if (sortKey === "review") {
        return (
          REVIEW_STATUS_OPTIONS.findIndex((option) => option.value === a.reviewStatus) -
          REVIEW_STATUS_OPTIONS.findIndex((option) => option.value === b.reviewStatus)
        );
      }

      if (sortKey === "ai") {
        return AI_REVIEW_OPTIONS.findIndex((option) => option.value === a.aiReviewStatus) -
          AI_REVIEW_OPTIONS.findIndex((option) => option.value === b.aiReviewStatus);
      }

      if (sortKey === "dsl") {
        return DSL_STATUS_OPTIONS.findIndex((option) => option.value === a.dslStatus) -
          DSL_STATUS_OPTIONS.findIndex((option) => option.value === b.dslStatus);
      }

      if (sortKey === "provenance") {
        return (
          (mapSpecProvenance(a.source, a.specSource) ?? "").localeCompare(
            mapSpecProvenance(b.source, b.specSource) ?? "",
            undefined,
            { sensitivity: "base" },
          )
        );
      }

      const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return dateB - dateA;
    });
  }, [filteredSections, sortKey]);

  const columns = useMemo<DataTableColumn<ProjectSpecSectionSummary>[]>(
    () => [
      { id: "title", header: "Title", accessor: (row) => row.title },
      { id: "category", header: "Category", accessor: (row) => row.category ?? row.kind ?? "-" },
      {
        id: "sequence",
        header: "#",
        accessor: (row) => row.sequence ?? "-",
        width: 60,
        align: "center",
      },
      {
        id: "reviewStatus",
        header: "Review",
        accessor: (row) => <RequirementReviewStatusBadge status={row.reviewStatus} />,
      },
      {
        id: "reviewActions",
        header: "Review actions",
        accessor: (row) => (
          <Select
            value={(row.reviewStatus as RequirementReviewStatus | undefined) ?? "pending_review"}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              event.stopPropagation();
              const nextStatus = event.target.value as RequirementReviewStatus;
              if (nextStatus === row.reviewStatus) return;
              void handleUpdateReviewStatus(row.id, nextStatus);
            }}
            disabled={updatingReviewId === row.id}
          >
            {REVIEW_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        ),
        width: 200,
      },
      {
        id: "aiReviewStatus",
        header: "AI Review",
        accessor: (row) => <AiReviewStatusBadge status={row.aiReviewStatus} />,
      },
      {
        id: "aiReviewActions",
        header: "Review actions",
        accessor: (row) => (
          <Select
            value={(row.aiReviewStatus as AiReviewStatus | undefined) ?? "none"}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              event.stopPropagation();
              const nextStatus = event.target.value as AiReviewStatus;
              if (nextStatus === row.aiReviewStatus) return;
              void handleUpdateAiReviewStatus(row.id, nextStatus);
            }}
            disabled={updatingAiReviewId === row.id}
          >
            {AI_REVIEW_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        ),
        width: 200,
      },
      {
        id: "dslStatus",
        header: "Execution",
        accessor: (row) => <ExecutionStatusBadge status={toExecutionStatus(row.dslStatus)} />,
      },
      {
        id: "dslActions",
        header: "Execution actions",
        accessor: (row) => (
          <Select
            value={row.dslStatus ?? "NOT_STARTED"}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              event.stopPropagation();
              const nextStatus = event.target.value;
              if (nextStatus === row.dslStatus) return;
              void handleUpdateDslStatus(row.id, nextStatus);
            }}
            disabled={updatingDslId === row.id}
          >
            {DSL_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        ),
        width: 180,
      },
      { id: "dslKind", header: "DSL", accessor: (row) => row.dslKind ?? "-" },
      {
        id: "source",
        header: "Source",
        accessor: (row) => row.source ?? row.specSource ?? row.specSourceId ?? "-",
      },
      {
        id: "updatedAt",
        header: "Updated",
        accessor: (row) => formatDate(row.updatedAt) || "-",
        width: 160,
      },
      {
        id: "actions",
        header: "Actions",
        accessor: (row) => (
          <DeleteButton
            size="sm"
            confirmMessage="Delete this section? This action cannot be undone."
            onDelete={() => handleDeleteSection(row.id)}
            isLoading={deletingSectionId === row.id}
            errorMessage={deleteErrorId === row.id ? deleteError : null}
            onClearError={() => {
              if (deleteErrorId === row.id) {
                setDeleteError(null);
                setDeleteErrorId(null);
              }
            }}
          />
        ),
        width: 140,
      },
    ],
    [
      deleteError,
      deleteErrorId,
      deletingSectionId,
      handleDeleteSection,
      handleUpdateReviewStatus,
      handleUpdateAiReviewStatus,
      handleUpdateDslStatus,
      updatingReviewId,
      updatingAiReviewId,
      updatingDslId,
    ],
  );

  const handleCreateClick = () => {
    setFormMode("create");
    setFormError(null);
    setFormState({
      title: "",
      category: "",
      source: "manual",
      reviewStatus: "pending_review",
      aiReviewStatus: "none",
      relatedRequirementIds: "",
      relatedChecklistItemIds: "",
      relatedWorkItemIds: "",
      relatedMeetingIds: "",
      relatedAiArtifactIds: "",
      content: "",
    });
    setIsFormOpen(true);
  };

  const handleEditClick = () => {
    if (!selectedSection) return;
    setFormMode("edit");
    setFormError(null);
    setFormState({
      title: selectedSection.title ?? "",
      category: selectedSection.category ?? "",
      source: selectedSection.source ?? "manual",
      reviewStatus: (selectedSection.reviewStatus ?? "pending_review") as RequirementReviewStatus,
      aiReviewStatus: (selectedSection.aiReviewStatus ?? "none") as AiReviewStatus,
      relatedRequirementIds: formatIdList(selectedSection.relatedRequirementIds),
      relatedChecklistItemIds: formatIdList(selectedSection.relatedChecklistItemIds),
      relatedWorkItemIds: formatIdList(selectedSection.relatedWorkItemIds ?? selectedSection.relatedWorkPackageIds),
      relatedMeetingIds: formatIdList(selectedSection.relatedMeetingIds),
      relatedAiArtifactIds: formatIdList(selectedSection.relatedAiArtifactIds),
      content: selectedSection.content ?? "",
    });
    setIsFormOpen(true);
  };

  const handleSubmit = async () => {
    if (!projectId) {
      setFormError("Project id is required");
      return;
    }

    setIsSaving(true);
    setFormError(null);

    const payload = toInputPayload(formState);

    try {
      const section =
        formMode === "edit" && selectedSection
          ? await updateProjectSpecSection(projectId, selectedSection.id, payload)
          : await createProjectSpecSection(projectId, payload);

      await loadSections(section.id);
      setIsFormOpen(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to save spec section", err);
      setFormError(err instanceof Error ? err.message : "Failed to save spec section");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PageHeader title="Spec sections" />
        <Button type="button" variant="primary" onClick={handleCreateClick}>
          Create spec section
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-600">Review</span>
          <Select
            value={selectedReviewStatus}
            onChange={(event) => setSelectedReviewStatus(event.target.value as RequirementReviewStatus | "all")}
          >
            <option value="all">All</option>
            {REVIEW_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-600">AI review</span>
          <Select
            value={selectedAiStatus}
            onChange={(event) => setSelectedAiStatus(event.target.value as AiReviewStatus | "all")}
          >
            <option value="all">All</option>
            {AI_REVIEW_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-600">Execution</span>
          <Select
            value={selectedDslStatus}
            onChange={(event) => setSelectedDslStatus(event.target.value as string | "all")}
          >
            <option value="all">All</option>
            {DSL_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-600">Provenance</span>
          <Select
            value={selectedProvenance}
            onChange={(event) => setSelectedProvenance(event.target.value as SpecProvenance | "all")}
          >
            {PROVENANCE_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-600">Sort by</span>
          <Select
            value={sortKey}
            onChange={(event) => setSortKey(event.target.value as typeof sortKey)}
          >
            <option value="updated">Last updated</option>
            <option value="review">Review</option>
            <option value="ai">AI review</option>
            <option value="dsl">Execution</option>
            <option value="provenance">Provenance</option>
          </Select>
        </div>
      </div>

      {error ? <InlineAlert tone="danger" title={error} /> : null}

      <div className="grid gap-4 lg:grid-cols-[1.05fr_0.95fr]">
        <Card>
          <DataTable<ProjectSpecSectionSummary>
            columns={columns}
            data={sortedSections}
            isLoading={isLoading}
            onRowClick={(row) => updateSelectedSection(row.id)}
            rowClassName={(row) => (row.id === selectedSectionId ? "data-table__row--active" : undefined)}
            emptyState={
              <EmptyState
                title="No spec sections"
                description="No spec sections found for this project yet."
              />
            }
          />
        </Card>

        <Card title={selectedSection ? selectedSection.title : "Section details"}>
          {!selectedSection ? (
            <div className="p-4 text-sm text-slate-600">Select a section to view its content.</div>
          ) : (
            <div className="flex flex-col gap-3 text-sm text-slate-800">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500">Section ID</div>
                  <div className="text-slate-900">{selectedSection.id}</div>
                </div>

                <Button type="button" variant="secondary" size="sm" onClick={handleEditClick}>
                  Edit
                </Button>
              </div>

              <div className="flex flex-col gap-2 rounded border border-slate-200 bg-slate-50 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <span>Review</span>
                    <RequirementReviewStatusBadge status={selectedSection.reviewStatus} />
                  </div>
                  <Select
                    value={(selectedSection.reviewStatus as RequirementReviewStatus | undefined) ?? "pending_review"}
                    onChange={(event) =>
                      void handleUpdateReviewStatus(selectedSection.id, event.target.value as RequirementReviewStatus)
                    }
                    disabled={updatingReviewId === selectedSection.id}
                  >
                    {REVIEW_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>
                {reviewError && updatingReviewId === selectedSection.id ? (
                  <span className="text-xs text-red-600">{reviewError}</span>
                ) : null}
              </div>

              <div className="flex flex-col gap-2 rounded border border-slate-200 bg-slate-50 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <span>AI review</span>
                    <AiReviewStatusBadge status={selectedSection.aiReviewStatus} />
                  </div>
                  <Select
                    value={(selectedSection.aiReviewStatus as AiReviewStatus | undefined) ?? "none"}
                    onChange={(event) =>
                      void handleUpdateAiReviewStatus(selectedSection.id, event.target.value as AiReviewStatus)
                    }
                    disabled={updatingAiReviewId === selectedSection.id}
                  >
                    {AI_REVIEW_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>
                {aiReviewError && updatingAiReviewId === selectedSection.id ? (
                  <span className="text-xs text-red-600">{aiReviewError}</span>
                ) : null}
              </div>

              <div className="flex flex-col gap-2 rounded border border-slate-200 bg-slate-50 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <span>Execution</span>
                    <ExecutionStatusBadge status={toExecutionStatus(selectedSection.dslStatus)} />
                    <span className="text-xs text-slate-500">{selectedSection.dslKind ?? "Unspecified"}</span>
                  </div>
                  <Select
                    value={selectedSection.dslStatus ?? "NOT_STARTED"}
                    onChange={(event) => void handleUpdateDslStatus(selectedSection.id, event.target.value)}
                    disabled={updatingDslId === selectedSection.id}
                  >
                    {DSL_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>
                {dslError && updatingDslId === selectedSection.id ? (
                  <span className="text-xs text-red-600">{dslError}</span>
                ) : null}
              </div>

              <div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500">Category</div>
                    <div className="text-slate-900">{selectedSection.category || selectedSection.kind || "-"}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500">Source</div>
                    <div className="text-slate-900">
                      {selectedSection.source ?? selectedSection.specSource ?? selectedSection.specSourceId ?? "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500">Sequence</div>
                    <div className="text-slate-900">{selectedSection.sequence ?? "-"}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500">Last updated</div>
                    <div className="text-slate-900">{formatDate(selectedSection.updatedAt) || "-"}</div>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500">Related requirements</div>
                  <div className="text-slate-900">{formatIdList(selectedSection.relatedRequirementIds) || "-"}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500">Checklist links</div>
                  <div className="text-slate-900">{formatIdList(selectedSection.relatedChecklistItemIds) || "-"}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500">Work items</div>
                  <div className="text-slate-900">
                    {formatIdList(selectedSection.relatedWorkItemIds ?? selectedSection.relatedWorkPackageIds) || "-"}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500">Meetings</div>
                  <div className="text-slate-900">{formatIdList(selectedSection.relatedMeetingIds) || "-"}</div>
                </div>
                <div className="md:col-span-2">
                  <div className="text-xs font-semibold uppercase text-slate-500">AI artifacts</div>
                  <div className="text-slate-900">{formatIdList(selectedSection.relatedAiArtifactIds) || "-"}</div>
                </div>
              </div>

              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-slate-700">DSL fragments</div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                    <span>Kind: {selectedSection.dslKind ?? "Unspecified"}</span>
                    <span>Status: {selectedSection.dslStatus ?? "NOT_STARTED"}</span>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="px-2 py-1 text-xs"
                      onClick={() => void handleSuggestDsl(selectedSection.id)}
                      disabled={suggestingDslId === selectedSection.id}
                    >
                      {suggestingDslId === selectedSection.id ? "Suggesting…" : "Suggest DSL"}
                    </Button>
                  </div>
                </div>
                {dslSuggestError && dslSuggestErrorId === selectedSection.id ? (
                  <div className="mt-1 text-xs text-red-600">{dslSuggestError}</div>
                ) : null}
                {dslAcceptError && dslAcceptErrorId === selectedSection.id ? (
                  <div className="mt-1 text-xs text-red-600">{dslAcceptError}</div>
                ) : null}

                <div className="mt-2 grid gap-3 md:grid-cols-2">
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500">Candidates</div>
                    {(selectedSection.dslCandidates ?? []).length === 0 ? (
                      <div className="text-sm text-slate-700">No candidate fragments.</div>
                    ) : (
                      <ul className="space-y-2 text-sm text-slate-800">
                        {(selectedSection.dslCandidates ?? []).map((fragment, index) => (
                          <li
                            key={`${selectedSection.id}-candidate-${index}`}
                            className="rounded border border-slate-200 bg-white p-2"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 space-y-1">
                                <div className="text-xs font-semibold uppercase text-slate-500">
                                  {fragment.kind ?? fragment.format ?? "Fragment"}
                                </div>
                                {fragment.summary ? (
                                  <div className="text-xs text-slate-600">{fragment.summary}</div>
                                ) : null}
                                <div className="whitespace-pre-wrap">
                                  {typeof fragment.value === "string"
                                    ? fragment.value
                                    : JSON.stringify(fragment, null, 2)}
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                className="px-2 py-1 text-xs"
                                onClick={() => void handleAcceptDsl(selectedSection.id, fragment)}
                                disabled={acceptingDslId === selectedSection.id}
                              >
                                {acceptingDslId === selectedSection.id
                                  ? "Accepting…"
                                  : "Accept fragment"}
                              </Button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500">Accepted</div>
                    {selectedSection.dslAccepted ? (
                      <div className="space-y-1 rounded border border-slate-200 bg-white p-2 text-sm text-slate-800">
                        <div className="text-xs text-slate-500">{selectedSection.dslAccepted.kind ?? selectedSection.dslAccepted.format ?? "Accepted"}</div>
                        {selectedSection.dslAccepted.summary ? (
                          <div className="text-xs text-slate-600">{selectedSection.dslAccepted.summary}</div>
                        ) : null}
                        <div className="whitespace-pre-wrap">
                          {typeof selectedSection.dslAccepted.value === "string"
                            ? selectedSection.dslAccepted.value
                            : JSON.stringify(selectedSection.dslAccepted, null, 2)}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-slate-700">No accepted fragment yet.</div>
                    )}
                  </div>
                </div>
              </div>

              {selectedSection.summary ? (
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500">Summary</div>
                  <div className="whitespace-pre-line text-slate-900">{selectedSection.summary}</div>
                </div>
              ) : null}

              <div>
                <div className="text-xs font-semibold uppercase text-slate-500">Content</div>
                <div className="whitespace-pre-line rounded bg-slate-50 p-3 text-slate-900">
                  {selectedSection.content}
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      <SpecSectionForm
        open={isFormOpen}
        mode={formMode}
        values={formState}
        onChange={setFormState}
        onCancel={() => setIsFormOpen(false)}
        onSubmit={handleSubmit}
        error={formError}
        isSaving={isSaving}
      />
    </div>
  );
}
